/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { type Cell, type RowData } from '@tanstack/react-table';
import { useMemo } from 'react';

import { useDataGridContext } from '../data-grid-context';
import { computeDataGridRowStyles } from './evaluate';

/**
 * Returns computed inline style for a specific cell based on style rules in context.
 * Use this in custom BodyCell overrides to apply rule-based styling.
 */
export function useDataGridCellStyle<T extends RowData>(
  cell: Cell<T, unknown>,
): React.CSSProperties | undefined {
  const { styleRules, styleRuleValueConverters } = useDataGridContext<T>();

  return useMemo(() => {
    if (!styleRules || styleRules.length === 0) {
      return undefined;
    }
    const original = cell.row.original;
    const rowData =
      typeof original === 'object' && original !== null
        ? (original as Record<string, unknown>)
        : {};
    const { cellStyles } = computeDataGridRowStyles(
      styleRules,
      rowData,
      styleRuleValueConverters,
    );
    return cellStyles[cell.column.id];
  }, [styleRules, styleRuleValueConverters, cell]);
}
